import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import CreditLogo from "../../assets/CreditLogo1.svg";
import {
  storeAccessToken,
  storeToken,
  storeUserInfo,
} from "../../utils/auth-token-utils/cookie";
import Spinner from "../../components/Spinner";
import routePaths from "../../router/route-paths";
import { showToast } from "../../store/slices/common";
import { login } from "../../http/auth";
import CustomTextField from "../../components/Textfield";
import { loginUser } from "../../comet-chat";

export default function SignIn() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  const validEmail = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;

  const handleSubmit = async (event) => {
    setLoading(true);
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = data.get("email");
    const password = data.get("password");

    if (!validEmail.test(email)) {
      dispatch(showToast({ color: "error", msg: "Invalid email" }));
      setLoading(false);
      return;
    }

    try {
      const response = await login({ email, password });
      const resData = response.data.data;

      if (resData) {
        await loginUser(resData);

        dispatch(
          showToast({ color: "success", msg: response.data.message?.en })
        );
        storeAccessToken(resData.token);
        navigate(routePaths.DASHBOARD, { state: { token: resData.token } });

        const remember = data.get("remember");

        if (remember) {
          storeToken(resData.token);
          storeUserInfo(resData);
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      {loading && <Spinner />}
      <Box
        sx={{
          paddingTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar
          alt="Credit Logo"
          src={CreditLogo}
          sx={{
            width: "100%",
            borderRadius: "0",
            height: "60px",
            marginLeft: 0,
            position: "relative",
            marginBottom: 2,
            top: 0,
            "& img": {
              objectFit: "unset",
            },
          }}
        />
        <Typography component="h1" variant="h3" margin="6px 0" left="10px">
          Sign in
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <CustomTextField
            required
            fullWidth
            id="email"
            placeholder="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            style={{ marginBottom: "20px" }}
          />
          <CustomTextField
            margin="normal"
            required
            fullWidth
            name="password"
            placeholder="Password"
            id="password"
            autoComplete="current-password"
            type={showPassword ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <Link
                  component="button"
                  variant="body1"
                  color="#156FFF"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </Link>
              ),
            }}
            style={{ marginBottom: "10px" }}
          />
          {
            <Link
              href="/reset-password"
              variant="body1"
              color="#156FFF"
              sx={{
                textDecoration: "none",
                fontSize: "16px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              Forgot password?
            </Link>
          }
          <FormControlLabel
            name="remember"
            control={
              <Checkbox
                value="remember"
                sx={{ color: "#0832DE", "& svg": { fill: "#0832DE" } }}
              />
            }
            label="Remember me"
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              background: "#156FFF",
              borderRadius: "61px",
              "&:hover": { background: "#156FFF" },
            }}
          >
            Sign In
          </Button>
          <Grid container>
            <Grid item xs></Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
}
